import { parseJwtToken, type ParsedJwtToken } from '../jwt/parseJwtToken';
import { AUTH_USER_ROLE } from './constants';

/**
 * Check if user is authenticated (not anonymous)
 *
 * Accepts either a raw JWT string or a pre-parsed token for performance optimization.
 * Checks the JWT payload to determine if the user has an authenticated role:
 * - ROLE_USER = authenticated user
 * - ROLE_EMPLOYEE = authenticated employee
 * - ROLE_ANONYMOUS = anonymous user (not logged in)
 * - Missing/invalid JWT = not authenticated
 *
 * @param token - JWT token string OR pre-parsed ParsedJwtToken
 * @returns true if user has USER or EMPLOYEE role, false otherwise
 *
 * @example
 * ```typescript
 * // Existing usage (backward compatible)
 * const jwt = cookies().get('pixum-api-jwt')?.value;
 * const authenticated = isUserAuthenticated(jwt);
 *
 * // Optimized usage (avoid re-parsing)
 * const parsedToken = parseJwtToken(jwt);
 * const authenticated = isUserAuthenticated(parsedToken);
 *
 * if (authenticated) {
 *   // User is logged in
 * }
 * ```
 */
export function isUserAuthenticated(token: string | ParsedJwtToken | null | undefined): boolean {
  // If already parsed, use it; otherwise parse the string
  const parsedToken = typeof token === 'string' ? parseJwtToken(token) : token;

  if (!parsedToken) {
    return false;
  }

  return parsedToken.role === AUTH_USER_ROLE.USER || parsedToken.role === AUTH_USER_ROLE.EMPLOYEE;
}
